import { useEffect, useState, ReactNode } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

export const AuthErrorBoundary = ({ children }: { children: ReactNode }) => {
  const [hasAuthError, setHasAuthError] = useState(false);
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    // Listen for auth errors globally
    const handleAuthError = (event: CustomEvent) => {
      if (event.detail?.status === 401 || event.detail?.status === 403) {
        setHasAuthError(true);
      }
    };

    window.addEventListener('auth-error', handleAuthError as EventListener);
    return () => window.removeEventListener('auth-error', handleAuthError as EventListener);
  }, []);

  useEffect(() => {
    if (hasAuthError && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (hasAuthError && countdown === 0) {
      // V2: Just signOut - guards will handle redirect
      // Per theme-architecture.md: Full reloads cause theme bootstrap re-run → flicker
      supabase.auth.signOut();
    }
  }, [hasAuthError, countdown]);

  if (hasAuthError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md p-8">
          <div className="space-y-6">
            <div className="flex flex-col items-center text-center space-y-2">
              <AlertCircle className="w-12 h-12 text-destructive" />
              <h2 className="text-2xl font-bold">Session Expired</h2>
              <p className="text-muted-foreground">
                Your session has expired. You will be signed out in {countdown} seconds...
              </p>
            </div>

            <Button 
              onClick={() => supabase.auth.signOut()} 
              className="w-full"
            >
              Sign In Now
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
};